import type { McpAuthType } from "./types";

const SHELL_CONTROL_CHARS = /[\n\r\t]/;

export function buildGatewayUrl(enginePublicUrl: string, slug: string): string {
  if (!slug || typeof slug !== "string" || slug.trim() === "") {
    throw new Error("Gateway slug is required and must be a non-empty string");
  }
  const trimmedSlug = slug.trim();
  return `${enginePublicUrl}/mcp/${trimmedSlug}`;
}

function encodeBase64(str: string): string {
  if (typeof btoa !== "function") {
    throw new Error(
      "Base64 encoding requires a browser environment (btoa not available). " +
        "Deep link builders are client-only."
    );
  }

  try {
    const utf8Bytes = new TextEncoder().encode(str);
    const binaryString = Array.from(utf8Bytes, (byte) =>
      String.fromCharCode(byte)
    ).join("");
    return btoa(binaryString);
  } catch (error) {
    throw new Error(
      `Failed to encode string to base64: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }
}

function validateName(name: string): void {
  if (!name || typeof name !== "string" || name.trim() === "") {
    throw new Error("Name is required and must be a non-empty string");
  }
}

function validateAuthType(authType: McpAuthType): void {
  const validAuthTypes: McpAuthType[] = [
    "arcade",
    "arcade_header",
    "user_source",
  ];
  if (!validAuthTypes.includes(authType)) {
    throw new Error(
      `Invalid auth type: ${authType}. Must be one of: ${validAuthTypes.join(", ")}`
    );
  }
}

/** cursor://anysphere.cursor-deeplink/mcp/install?name=X&config=base64(...) */
export function buildCursorDeepLink(
  enginePublicUrl: string,
  name: string,
  slug: string
): string {
  validateName(name);
  const config = { url: buildGatewayUrl(enginePublicUrl, slug) };
  const encodedName = encodeURIComponent(name);
  const encodedConfig = encodeURIComponent(
    encodeBase64(JSON.stringify(config))
  );

  return `cursor://anysphere.cursor-deeplink/mcp/install?name=${encodedName}&config=${encodedConfig}`;
}

/** vscode:mcp/install?{"name":"...","type":"http","url":"..."} — single URL-encoded JSON as query */
export function buildVscodeDeepLink(
  enginePublicUrl: string,
  name: string,
  slug: string
): string {
  validateName(name);
  const config = {
    name,
    type: "http" as const,
    url: buildGatewayUrl(enginePublicUrl, slug),
  };
  return `vscode:mcp/install?${encodeURIComponent(JSON.stringify(config))}`;
}

export function buildMcpServersJson(
  enginePublicUrl: string,
  name: string,
  slug: string,
  authType: McpAuthType,
  userId?: string
): string {
  validateAuthType(authType);
  const url = buildGatewayUrl(enginePublicUrl, slug);
  const safeName = sanitizeJsonPropertyName(name);

  const serverConfig: Record<string, unknown> = { url };

  if (authType === "arcade_header") {
    serverConfig.headers = {
      Authorization: "Bearer <your-api-key>",
      "Arcade-User-ID": userId ?? "<user-identifier>",
    };
  }

  const config = {
    mcpServers: {
      [safeName]: serverConfig,
    },
  };

  return JSON.stringify(config, null, 2);
}

export function buildVscodeJson(
  enginePublicUrl: string,
  name: string,
  slug: string,
  authType: McpAuthType,
  userId?: string
): string {
  validateAuthType(authType);
  const url = buildGatewayUrl(enginePublicUrl, slug);
  const safeName = sanitizeJsonPropertyName(name);

  const serverConfig: Record<string, unknown> = {
    type: "http",
    url,
  };

  if (authType === "arcade_header") {
    serverConfig.headers = {
      Authorization: "Bearer <your-api-key>",
      "Arcade-User-ID": userId ?? "<user-identifier>",
    };
  }

  const config = {
    servers: {
      [safeName]: serverConfig,
    },
  };

  return JSON.stringify(config, null, 2);
}

function escapeYamlValue(str: string): string {
  return `"${str
    .replace(/\\/g, "\\\\")
    .replace(/"/g, '\\"')
    .replace(/\n/g, "\\n")
    .replace(/\r/g, "\\r")
    .replace(/\t/g, "\\t")}"`;
}

export function createSafeKey(name: string, separator: "-" | "_"): string {
  const safeKey = name
    .toLowerCase()
    .replace(/\s+/g, separator)
    .replace(new RegExp(`[^a-z0-9${separator}]`, "g"), "");

  return safeKey || `arcade${separator}gateway`;
}

export function buildGooseYaml(
  enginePublicUrl: string,
  name: string,
  slug: string,
  authType: McpAuthType,
  userId?: string
): string {
  validateAuthType(authType);
  validateName(name);
  const url = buildGatewayUrl(enginePublicUrl, slug);
  const safeKey = createSafeKey(name, "-");
  const escapedName = escapeYamlValue(name);
  const escapedUrl = escapeYamlValue(url);

  if (authType === "arcade_header") {
    const escapeduserId = escapeYamlValue(userId ?? "<user-identifier>");
    return `extensions:
  ${safeKey}:
    name: ${escapedName}
    url: ${escapedUrl}
    type: streamable_http
    headers:
      Authorization: "Bearer <your-api-key>"
      Arcade-User-ID: ${escapeduserId}
    enabled: true
    timeout: 300`;
  }

  return `extensions:
  ${safeKey}:
    name: ${escapedName}
    url: ${escapedUrl}
    type: streamable_http
    enabled: true
    timeout: 300`;
}

function escapeShell(str: string): string {
  if (!SHELL_CONTROL_CHARS.test(str)) {
    return `'${str.replace(/'/g, "'\\''")}'`;
  }

  const escaped = str
    .replace(/\\/g, "\\\\")
    .replace(/'/g, "\\'")
    .replace(/\n/g, "\\n")
    .replace(/\r/g, "\\r")
    .replace(/\t/g, "\\t");

  return `$'${escaped}'`;
}

export function buildClaudeCodeCommand(
  enginePublicUrl: string,
  name: string,
  slug: string,
  authType: McpAuthType,
  userId?: string
): string {
  validateAuthType(authType);
  validateName(name);
  const url = buildGatewayUrl(enginePublicUrl, slug);
  const safeName = createSafeKey(name, "-");
  const escapedUrl = escapeShell(url);

  if (authType === "arcade_header") {
    const userIdValue = userId ?? "<user-identifier>";
    const escapeduserIdHeader = escapeShell(`Arcade-User-ID: ${userIdValue}`);
    return `claude mcp add ${safeName} --transport http ${escapedUrl} --header "Authorization: Bearer <your-api-key>" --header ${escapeduserIdHeader}`;
  }

  return `claude mcp add ${safeName} --transport http ${escapedUrl}`;
}

function escapeTOML(str: string): string {
  return str
    .replace(/\\/g, "\\\\")
    .replace(/"/g, '\\"')
    .replace(/\n/g, "\\n")
    .replace(/\r/g, "\\r")
    .replace(/\t/g, "\\t");
}

export function buildCodexToml(
  enginePublicUrl: string,
  name: string,
  slug: string,
  authType: McpAuthType,
  userId?: string
): string {
  validateAuthType(authType);
  validateName(name);
  const url = buildGatewayUrl(enginePublicUrl, slug);
  const safeKey = createSafeKey(name, "_");
  const escapedUrl = escapeTOML(url);

  if (authType === "arcade_header") {
    const escapeduserId = escapeTOML(userId ?? "<user-identifier>");
    return `[mcp_servers.${safeKey}]
url = "${escapedUrl}"
http_headers = { Authorization = "Bearer <your-api-key>", "Arcade-User-ID" = "${escapeduserId}" }`;
  }

  return `[mcp_servers.${safeKey}]
url = "${escapedUrl}"`;
}

function sanitizeJsonPropertyName(name: string): string {
  if (!name || typeof name !== "string" || name.trim() === "") {
    throw new Error("Name is required and must be a non-empty string");
  }

  const trimmed = name.trim();
  const dangerousNames = ["__proto__", "constructor", "prototype"];
  if (dangerousNames.includes(trimmed.toLowerCase())) {
    throw new Error(
      `Property name "${trimmed}" is not allowed for security reasons`
    );
  }

  return trimmed;
}
